import { getLang } from '@/helpers/getLang'
import { setJwtToken } from '@/helpers/setRequestHeaders'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export enum ArticleTypes {
    Article = "article",
    UsefulMaterial = "usefulMaterials",
    Event = "event",
    Vacancy = "vacancy",
    Webinar = "webinar"
}

export interface ArticleTag {
    id: number
    title: string
    value: string
}

export interface EventArticle {

    id: number,
    title: string,
    description: any,
    views: number,
    creation_date: string,
    photos: string[],
    videos: string,
    type: string,
    preview: string,
    intro: string,
    translit: string,
    subtitle: string,
    keywords: string[],
    likes: number,
    likesSessions: [],
    dateFrom: string,
    dateTo: string,
    location: string,
    link: string
    enableToView: boolean
}

export type NewsItem = {
    id: number
    title: string
    description: string
    views: number
    creation_date: string
    responsible_id: {
        id: number
        name: string
        position: number
        photo: string
    }[]
    likes: number,
    card_id: number[]
    photos: string[]
    videos: string
    tags_id: ArticleTag[]
    preview: string
    intro: string
    products_id: number[]
    type: string
    subtitle: string
    translit?: string
    enableToView: boolean
}

interface AddArticleDto {
    title: string
    description: string
    responsible_id: number[]
    card_id: number[]
    photos: string[]
    videos: string
    tags_id: number[]
    preview: string
    intro: number
    products_id: number[]
    type?: ArticleTypes.Article | ArticleTypes.UsefulMaterial | ArticleTypes.Event | ArticleTypes.Vacancy
    subtitle: string,
    useful_materials_category?: any,
    dateFrom?: string,
    dateTo?: string,
    location?: string,
    link?: string
}

interface UpdateArticleDto {
    id: number
    title: string
    description: string
    responsible_id: number[]
    card_id: number[]
    photos: string[]
    videos: string
    tags_id: number[]
    preview: string
    intro: number
    products_id: number[]
    type?: ArticleTypes.Article | ArticleTypes.UsefulMaterial | ArticleTypes.Event | ArticleTypes.Vacancy
    subtitle: string,
    useful_materials_category?: any,
    dateFrom?: string,
    dateTo?: string,
    location?: string,
    link?: string
}

export type NewsFilterType = {
    get: number,
    page: number,
    tags: number[],
    title: string
}

export const newsApi = createApi({
    reducerPath: 'newsApi',
    tagTypes: ['VACANCY_REGIONS', 'VACANCY_SPECIALIZATIONS', 'ARTICLE'],
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.NEXT_PUBLIC_API_ENDPOINT || 'http://megakorm.ru:3005/api',
        prepareHeaders: setJwtToken
    }),
    endpoints: builder => ({
        getNew: builder.query<NewsItem, { id: number }>({
            query: ({ id }) => ({ url: `/articles/${id}` }),
            providesTags: ["ARTICLE"],
        }),
        getAllNews: builder.query<NewsItem[], { count: number; page: number }>({
            query: ({ count, page }) => ({ url: `/articles/pages?get=${count}&page=${page}` }),
            keepUnusedDataFor: 1,
            providesTags: ["ARTICLE"],
        }),
        getExhibitions: builder.query<NewsItem[], void>({
            query: () => ({url: "/articles/tags/exhibition"}),
            providesTags: ["ARTICLE"]
        }),
        getAllNewsEn: builder.query<NewsItem[], { count: number; page: number }>({
            query: ({ count, page }) => ({ url: `/articles/pages?get=${count}&page=${page}&lang=en` }),
            keepUnusedDataFor: 1,
            providesTags: ["ARTICLE"],
        }),
        getAllNewsOnly: builder.query<NewsItem[], { count: number; page: number }>({
            query: ({ count, page }) => ({ url: `/articles/articles/pages?get=${count}&page=${page}&lang=${getLang()}` }),
            keepUnusedDataFor: 1
        }),
        getEvents: builder.query<EventArticle[], void>({
            query: () => ({ url: `/articles/events/all?lang=${getLang()}` })
        }),
        createNew: builder.mutation<any, AddArticleDto>({
            query: data => ({ url: `/articles`, method: 'POST', body: data }),
            invalidatesTags: ["ARTICLE"]
        }),
        updateNew: builder.mutation<any, UpdateArticleDto>({
            query: data => ({ url: `/articles/${data.id}`, method: 'PATCH', body: data }),
            invalidatesTags: ["ARTICLE"]
        }),
        updateEnNew: builder.mutation<any, {title: string, description: string, subtitle: string, id: number}>({
            query: data => ({ url: `/articles/articleEn/update/${data.id}`, method: 'PATCH', body: {title: data.title, description: data.description, subtitle: data.subtitle} }),
            invalidatesTags: ["ARTICLE"]
        }),
        filterNews: builder.mutation<NewsFilterType, any>({
            query: (body) => ({ url: `/articles/articles/pages/filter/count?lang=${getLang()}`, method: "POST", body: body })
        }),
        getMostViewedNews: builder.query<NewsItem[], { type: string }>({
            query: ({ type }) => ({ url: `/articles/articles/by-views?type=${type}&lang=${getLang()}` })
        }),
        getMostNewedNews: builder.query<NewsItem[], { type: string }>({
            query: ({ type }) => ({ url: `/articles/articles/by-id?type=${type}&lang=${getLang()}` })
        }),
        addView: builder.query({
            query: ({ id }) => ({
                url: `articles/view/${id}`,
                method: 'GET'
            })
        }),
        deleteArticlesByIds: builder.mutation<any, any>({
            query: (data) => ({ url: `/articles/delete-by-ids/del`, method: "POST", body: data }),
            invalidatesTags: ["ARTICLE"]
        }),
        getNewByTranslit: builder.query<NewsItem, { id: string }>({
            query: ({ id }) => ({ url: `/articles/by-translit/${id}?lang=${getLang()}` })
        }),
        getVacancyRegions: builder.query<{id: number, title: string}[], void>({
            query: () => ({ url: '/vacancy-regions' }),
            providesTags: result => ['VACANCY_REGIONS']
        }),
        createVacancyRegion: builder.mutation<any, {title: string}>({
            query: data => ({ url: `/vacancy-regions`, method: 'POST', body: data }),
            invalidatesTags: ["VACANCY_REGIONS"]
        }),
        deleteVacancyRegionById: builder.mutation<any, any>({
            query: (data) => ({ url: `/vacancy-regions/${data}`, method: "DELETE" }),
            invalidatesTags: ["VACANCY_REGIONS"]
        }),
        updateVacancyRegion: builder.mutation<any, {title: string, id: number}>({
            query: data => ({ url: `/vacancy-regions/${data.id}`, method: 'PATCH', body: {title: data.title} }),
            invalidatesTags: ["VACANCY_REGIONS"]
        }),
        getVacancySpecializations: builder.query<{id: number, title: string}[], void>({
            query: () => ({ url: '/vacancy-specializations' }),
            providesTags: result => ['VACANCY_SPECIALIZATIONS']
        }),
        createVacancySpecialization: builder.mutation<any, {title: string}>({
            query: data => ({ url: `/vacancy-specializations`, method: 'POST', body: data }),
            invalidatesTags: ["VACANCY_SPECIALIZATIONS"]
        }),
        deleteVacancySpecializationById: builder.mutation<any, any>({
            query: (data) => ({ url: `/vacancy-specializations/${data}`, method: "DELETE" }),
            invalidatesTags: ["VACANCY_SPECIALIZATIONS"]
        }),
        updateVacancySpecialization: builder.mutation<any, {title: string, id: number}>({
            query: data => ({ url: `/vacancy-specializations/${data.id}`, method: 'PATCH', body: {title: data.title} }),
            invalidatesTags: ["VACANCY_SPECIALIZATIONS"]
        }),
        toggleEnableToViewArticle: builder.mutation<any, {id: number}>({
            query: (data) => ({url: "/articles/enable-to-view", method: "POST", body: data}),
            invalidatesTags: ["ARTICLE"],
        }),
        toggleEnableToViewByListArticle: builder.mutation<any, {ids: number[], enable?: boolean}>({
            query: (data)=>({url: "/articles/enable-to-view/list", method: "POST", body: data}),
            invalidatesTags: ["ARTICLE"],
        })
    })
})

export const {
    useGetNewQuery,
    useGetAllNewsQuery,
    useGetExhibitionsQuery,
    useGetAllNewsEnQuery,
    useFilterNewsMutation,
    useCreateNewMutation,
    useUpdateNewMutation,
    useUpdateEnNewMutation,
    useGetMostViewedNewsQuery,
    useAddViewQuery,
    useDeleteArticlesByIdsMutation,
    useGetAllNewsOnlyQuery,
    useGetNewByTranslitQuery,
    useGetEventsQuery,
    useGetVacancyRegionsQuery,
    useGetVacancySpecializationsQuery,
    useCreateVacancyRegionMutation,
    useCreateVacancySpecializationMutation,
    useDeleteVacancyRegionByIdMutation,
    useUpdateVacancyRegionMutation,
    useDeleteVacancySpecializationByIdMutation,
    useUpdateVacancySpecializationMutation,
    useToggleEnableToViewArticleMutation,
    useToggleEnableToViewByListArticleMutation,
    useGetMostNewedNewsQuery
} = newsApi
